import { SILENCE_AUDIO_URL } from "@/lib/constants";
import { Howl, Howler } from "howler";

export class AudioMixer {
    ambienceVolume: number;
    speechVolume: number;
    sfxVolume: number;
    speechPlaybackRate: number;

    silenceSound: Howl | null;
    ambienceSoundUrl: string | null;
    ambienceSound: Howl | null;
    speechSound: Howl | null;
    speechSoundUrl: string | null;
    
    sfxSounds: Record<string, Howl> = {};

    preloadedUrls: Set<string> = new Set();

    constructor() {
        this.ambienceVolume = 0.35;
        this.speechVolume = 1;
        this.sfxVolume = 0.5;
        this.speechPlaybackRate = 1;
        Howler.autoSuspend = false;

        // this.initSilenceSound();
    }

    resume = async () => {
        if (Howler.ctx != null && Howler.ctx.state !== "running") {
            try {
                // Howler usually unlocks automatically, but we double-ensure
                await Howler.ctx.resume?.();
            } catch { }
        }
    };

    initSilenceSound = () => {
        try {
            this.silenceSound = new Howl({
                src: [SILENCE_AUDIO_URL],
                loop: false,
                volume: 0,
                html5: false,
                preload: true
            });
    
            this.silenceSound.play();
        } catch { 
            setTimeout(() => {
                this.initSilenceSound();
            }, 5000);
        }
    };

    startAmbience = async (url: string, volume: number = this.ambienceVolume) => {
        if (this.ambienceSound != null) {
            if (this.ambienceSoundUrl === url) {
                return;
            } else {
                this.ambienceSound.stop();
                this.ambienceSound.unload();
                this.ambienceSound = null;
                this.ambienceSoundUrl = null;
            }
        } else {
            this.ambienceSound = new Howl({
                src: [url],
                loop: true,
                volume: volume,
                html5: false,
                preload: true
            });
        }

        await this.ambienceSound.play();
        this.ambienceSound.volume(volume);
        this.ambienceSoundUrl = url;
    };

    stopAmbience = () => {
        if (this.ambienceSound != null) {
            this.ambienceSound.stop();
            this.ambienceSound.unload();
            this.ambienceSound = null;
            this.ambienceSoundUrl = null;
        }
    };

    playSpeech = async (url: string, volume: number | null = null, onEnded?: () => void, onPlay?: () => void, onLoadError?: () => void, onStop?: () => void) => {        
        if (this.speechSound != null) {
            this.speechSound.stop();

            if (this.speechSoundUrl != url) {
                this.speechSound.unload();
                this.speechSound = null;
                this.speechSoundUrl = null;
            }
        } 

        const endTracker = {};

        this.speechSound = new Howl({
            src: [url],
            volume: volume ?? this.speechVolume,
            onend: (soundId) => {
                endTracker[soundId] = true;
                if (onEnded) {
                    onEnded();
                }
            },
            onplay: onPlay,
            onstop: onStop, // onStop is called when the speech stops and/or ends as
            onloaderror: onLoadError,
            loop: false,
            html5: false,
            preload: true,
            rate: this.speechPlaybackRate,            
        });

        await this.speechSound.play();
        this.speechSound.rate(this.speechPlaybackRate);
        this.speechSoundUrl = url;
    };

    isSpeechPlaying = (): boolean => {
        if (this.speechSound != null) {
            return this.speechSound.playing();
        }
        return false;
    };

    stopSpeech = () => {
        if (this.speechSound != null) {
            this.speechSound.stop();
            this.speechSound.unload();
            this.speechSound = null;
            this.speechSoundUrl = null;
        }
    };

    setSpeechPlaybackRate = (rate: number) => {
        this.speechPlaybackRate = rate;

        if (this.speechSound != null) {
            this.speechSound.rate(rate);
        }
    };

    getSpeechPlaybackRate = (): number => {
        if (this.speechSound != null) {
            return this.speechSound.rate();
        }
        return 1;
    };

    playSfx = async (url: string, volume: number | null = null, onStart?: () => void, onEnded?: () => void) => {
        if (this.sfxSounds[url] != null) {
            this.sfxSounds[url].stop();
            this.sfxSounds[url].unload();
            this.sfxSounds[url] = null;
        }
    
        this.sfxSounds[url] = new Howl({
            src: [url],
            volume: volume ?? this.sfxVolume,
            onplay: onStart,
            onend: onEnded,
            loop: false,
            html5: false,
            preload: true
        });

        await this.sfxSounds[url].play();
    };

    stopSfx = (url: string, unload: boolean = false) => {
        if (this.sfxSounds[url] != null) {
            this.sfxSounds[url].stop();

            if (unload) {
                this.sfxSounds[url].unload();
                this.sfxSounds[url] = null;
                delete this.sfxSounds[url];
            }
        }
    };

    preloadSfx = (url: string) => {
        if (this.preloadedUrls.has(url)) {
            return;
        }

        this.preloadedUrls.add(url);

        const preloadedSound = new Howl({
            src: [url],
            preload: true
        });        
    };
}
